import React, { useState, useEffect } from 'react';
import { Text, TouchableOpacity, View, StyleSheet, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import _ from 'lodash';
import Fonts from 'src/core/utils/Fonts';
import Dimens from 'src/core/utils/Dimens';
import Colors from 'src/core/utils/Colors';
import FieldError from 'src/presentation/components/atoms/FieldError';
import Translations from 'src/core/utils/Translations';
import errorHandler from 'src/core/utils/errorHandler';
import { ChoiceInfo, ChoiceDetailLink } from 'src/presentation/components/molecules/ProgramChoice';
import { choiceGroups } from 'src/presentation/components/molecules/ProgramChoiceGroups';
import type { ApiFormAnswer, ApiFormQuestion, ApiFormQuestionOption } from 'src/core/models/api/shared/ApiFormQuestion';
import { dayLimitReached, limitReached, maxChoicesReached, overlapWith } from 'src/core/utils/formChoiceLimits';

interface CheckBoxListProps {
	name: string;
	data: ApiFormQuestion & { show_limit?: boolean };
	answers?: Record<string, ApiFormAnswer>;
	onCheck: (values: string[]) => void;
	formID?: string | number;
	values?: ApiFormAnswer;
	error?: string;
}

function CheckBoxList({ name, data, answers, onCheck, error }: CheckBoxListProps) {
	const rawOptions: ApiFormQuestionOption[] = data.options ?? [];
	// A checkbox question without options is a single yes/no checkbox: use the label as the option, so a real
	// checkbox is shown instead of just the title.
	const isSingleCheckbox = rawOptions.length === 0;
	const options: ApiFormQuestionOption[] = isSingleCheckbox
		? [{ name: data.label, limit: null, total_submissions: 0 }]
		: rawOptions;

	const buildCheckboxStates = (ans?: Record<string, ApiFormAnswer>): Record<string, boolean> => {
		const states: Record<string, boolean> = {};
		const current = ans?.[name];
		const selected = Array.isArray(current) ? current : [];
		options.forEach((option) => {
			states[option.name] = selected.includes(option.name);
		});
		return states;
	};

	const [checkboxStates, setCheckboxStates] = useState<Record<string, boolean>>(() => buildCheckboxStates(answers));

	useEffect(() => {
		if (!_.isEmpty(answers) && name in (answers ?? {})) {
			setCheckboxStates(buildCheckboxStates(answers));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [answers]);

	const chosen = options.filter((option) => checkboxStates[option.name]);
	const chosenCount = chosen.length;

	// Why this option cannot be ticked, or null when it can. Unticking is always
	// allowed: a participant who reached a limit must still be able to change his
	// mind.
	const blockedBy = (option: ApiFormQuestionOption): string | null => {
		if (checkboxStates[option.name]) return null;
		if (limitReached(option)) return Translations.textChoiceFull;
		if (maxChoicesReached(data, chosenCount)) return Translations.textMaxChoices(data.max_choices ?? 0);
		if (dayLimitReached(data, option, chosen))
			return Translations.textDayFull(option.day_label ?? '', data.max_per_day ?? 1);

		const clash = overlapWith(data, option, chosen);

		return clash === null ? null : Translations.textOverlaps(clash.name);
	};

	const toggleCheckBox = (option: ApiFormQuestionOption) => {
		const ticking = !checkboxStates[option.name];

		if (ticking) {
			const reason = blockedBy(option);

			if (reason !== null) {
				errorHandler(Translations.textChoiceNotPossible, reason);

				return;
			}
		}

		const currentValues: string[] = [];
		options.forEach((opt) => {
			if (opt.name === option.name) {
				if (!checkboxStates[option.name]) {
					currentValues.push(opt.name);
				}
			} else {
				if (checkboxStates[opt.name]) {
					currentValues.push(opt.name);
				}
			}
		});

		setCheckboxStates((prev) => ({
			...prev,
			[option.name]: !prev[option.name],
		}));

		onCheck(currentValues);
	};

	if (!options) return null;

	const renderOption = (option: ApiFormQuestionOption, index: number) => {
		const checked = checkboxStates[option.name];
		const isDisabled = !checked && (limitReached(option) || maxChoicesReached(data, chosenCount));

		return (
			<TouchableOpacity
				key={index}
				style={[
					{
						flexDirection: 'row',
						paddingHorizontal: Dimens.padding8,
						paddingVertical: Dimens.padding12,
						alignItems: 'center',
						borderBottomWidth: 1,
						borderBottomColor: Colors.greyBorder,
					},
					StyleSheet.flatten(isDisabled && styles.listitemDisabled),
				]}
				onPress={() => toggleCheckBox(option)}
			>
				<View>
					{checked ? (
						<View style={styles.checked}>
							<Ionicons name="checkmark" size={26} style={styles.icon} />
						</View>
					) : (
						<View style={styles.notchecked} />
					)}
				</View>

				<View style={{ flex: 1, paddingLeft: Dimens.padding16 }}>
					<Text style={styles.text}>
						{option.name}
						{isSingleCheckbox && data.required ? '*' : null}
					</Text>
					<ChoiceInfo data={data} option={option} withDay={!grouped} />
				</View>

				<ChoiceDetailLink option={option} />
			</TouchableOpacity>
		);
	};

	// Several days in one question: each day gets its own heading (#404).
	const groups = choiceGroups(options);
	const grouped = groups.length > 1;

	return (
		<View>
			{!isSingleCheckbox && (
				<View style={[styles.listTitle, { borderBottomWidth: 1, borderBottomColor: Colors.greyBorder }]}>
					<Text style={styles.question}>
						{data.label}
						{data.required ? '*' : null}
					</Text>
				</View>
			)}
			{error ? <FieldError message={error} /> : null}
			{groups.map((group, groupIndex) => (
				<View key={groupIndex}>
					{group.label ? <Text style={styles.dayHeading}>{group.label}</Text> : null}
					{group.options.map(renderOption)}
				</View>
			))}
		</View>
	);
}

export default CheckBoxList;

const styles = StyleSheet.create({
	text: {
		fontFamily: Fonts.montserratRegular,
		fontSize: Dimens.fontSize16,
		color: Colors.greyTextDark,
	},
	dayHeading: {
		fontFamily: Fonts.montserratBold,
		fontSize: Dimens.fontSize13,
		color: Colors.greyTextDark,
		paddingTop: Dimens.padding12,
		paddingBottom: Dimens.padding4,
		paddingHorizontal: Dimens.padding8,
	},
	listTitle: {
		// See RadioListField: spacing comes from fieldBlock, not from the title.
		paddingTop: Dimens.padding0,
		marginRight: -20,
		marginLeft: Dimens.margin0,
		paddingBottom: Dimens.padding8,
		borderBottomWidth: Platform.OS === 'android' ? 1 : undefined,
		borderBottomColor: Platform.OS === 'android' ? Colors.overlay42 : undefined,
		marginBottom: Platform.OS === 'android' ? Dimens.margin8 : undefined,
	},
	listitemDisabled: {
		opacity: 0.4,
	},
	question: {
		fontFamily: Platform.OS === 'android' ? Fonts.montserratRegular : Fonts.montserratBold,
		fontSize: Dimens.fontSize16,
		color: Colors.greyTextDark,
		lineHeight: 20,
	},
	checked: {
		height: Platform.OS === 'android' ? 20 : 15,
		width: Platform.OS === 'android' ? 20 : 15,
		borderWidth: Platform.OS === 'android' ? 2 : 1,
		borderColor: Colors.secondary,
		borderRadius: 2,
		alignItems: 'center',
		justifyContent: 'center',
		backgroundColor: Platform.OS === 'android' ? Colors.secondary : undefined,
	},
	notchecked: {
		height: Platform.OS === 'android' ? 20 : 15,
		width: Platform.OS === 'android' ? 20 : 15,
		borderWidth: Platform.OS === 'android' ? 2 : 1,
		borderColor: Platform.OS === 'android' ? Colors.greyPlatform : Colors.greyText,
		borderRadius: 2,
	},
	icon: {
		color: Platform.OS === 'android' ? Colors.white : Colors.secondary,
		marginLeft: Platform.OS === 'android' ? -4 : -4,
		marginTop: Platform.OS === 'android' ? -7 : -9,
	},
});
